import { create } from 'zustand'
import { createJSONStorage, persist, type StateStorage } from 'zustand/middleware'
import { env } from '@/config/env'
import type { ApplicationReason, OutstandingRequirement } from '@/config/constants'
import { endSession } from '@/lib/http/vfsclient/session'
import type {
  AddressSnapshot,
  ContactSnapshot,
  EligibleProduct,
  OfficerSnapshot,
  PaymentItem,
  PersonalDetails,
  SessionState,
  WizardStep,
} from '../types/session.types'

/**
 * The wizard's resumable state.
 *
 * Why a store at all: every step POSTs and gets a 204 back, and the DHA API offers
 * no endpoint to read submitted contact, address or personal details again. So a
 * refresh on step 3 would otherwise lose the NPR record fetched at step 1 with no
 * way to recover it. See docs/api-gaps.md #4 — a `GET /application/{ref}` would
 * remove the need for this entirely, and we should keep asking for it.
 *
 * The URL owns *position*; this store owns the minimum needed to resume.
 */

interface SessionActions {
  startApplication: (input: { idNo: string; availableProducts: EligibleProduct[] }) => void
  setSubmittedAt: (submittedAt: string) => void
  setFinaliseResult: (result: {
    outstandingRequirements: OutstandingRequirement[]
    totalPayable: number
    paymentItems: PaymentItem[]
  }) => void
  setAppRefNo: (appRefNo: string) => void
  setPersonalDetails: (details: PersonalDetails) => void
  setDeclarationsRequired: (codes: string[]) => void
  setReason: (reason: ApplicationReason) => void
  setContact: (contact: ContactSnapshot) => void
  setAddress: (address: AddressSnapshot) => void
  setOfficer: (officer: OfficerSnapshot) => void
  setVacAuthId: (vacAuthId: number) => void
  markVfsClientVerificationSaved: () => void
  completeStep: (step: WizardStep) => void
  recordFaceAttempt: (verified: boolean) => void
  setPhotoImg: (photoImg: string | undefined) => void
  setSignatureImg: (signatureImg: string | undefined) => void
  touch: () => void
  isExpired: () => boolean
  /**
   * Applies the TTL, rather than merely reporting it. Returns whether it fired.
   *
   * `isExpired()` alone was only ever read during a guard's render, and nothing
   * acted on it — so an expired session kept its appRefNo, RequireSession bounced
   * it to `/apply`, and the resume selector (which cannot see the TTL) sent it
   * straight back to the step it came from, forever. Making expiry a state
   * TRANSITION is what closes that: an expired session becomes an EMPTY one, and
   * `firstIncompleteFlowStep` already resolves an empty session to the ID-number
   * screen. One terminal destination, no cycle.
   */
  expireIfStale: () => boolean
  /**
   * The same transition, on the server's say-so rather than the clock's: the
   * transport reports (`onSessionLost`) that the backend no longer recognises this
   * tab's session, or that it has been re-bound to another application. The
   * `submittedAt` exemption still holds — see `expireIfStale`.
   */
  expireNow: () => boolean
  /** Drops the `expiredAt` banner flag once the citizen has been told. */
  clearExpiredFlag: () => void
  /** Drops the applicant's personal data but keeps the receipt viewable. */
  clearApplicantData: () => void
  clear: () => void
}

export type SessionStore = SessionState & SessionActions

/**
 * Every optional field is listed explicitly as `undefined`.
 *
 * zustand's `set` merges rather than replaces, so a key absent from this object
 * would survive `clear()` — which for `idNo` or `personalDetails` means one
 * citizen's details leaking into the next application started in the same tab.
 */
const initialState: SessionState = {
  appRefNo: undefined,
  idNo: undefined,
  submittedAt: undefined,
  availableProducts: undefined,
  personalDetails: undefined,
  declarationsRequired: undefined,
  reason: undefined,
  contact: undefined,
  address: undefined,
  officer: undefined,
  vacAuthId: undefined,
  vfsClientVerificationSaved: undefined,
  completedSteps: [],
  faceAttempts: 0,
  faceVerified: false,
  faceBlocked: false,
  lastActivityAt: undefined,
  expiredAt: undefined,
  outstandingRequirements: undefined,
  totalPayable: undefined,
  paymentItems: undefined,
  photoImg: undefined,
  signatureImg: undefined,
}

/** The key the persist middleware writes under. Exported so tests can assert on it. */
export const SESSION_STORAGE_KEY = 'id-reissue.session'

/**
 * Storage can be emptied out from under a running tab.
 *
 * DevTools' "Clear site data", a browser's own "clear browsing data", a private-mode
 * eviction — all of them wipe sessionStorage without touching the copy this store
 * holds in memory. zustand's persist middleware then writes the whole session
 * straight back on the very next `set`, and a finalise landing a moment later, or the
 * expiry watcher's `touch()`, is enough to do it. The citizen's details are back on
 * disk, the next load rehydrates them, and the resume selector sends the browser to
 * `/apply/<ref>/submitted` and starts calling the API for an application the citizen
 * has just asked to be forgotten.
 *
 * So the write is where this is caught. Every persisted write goes through the
 * adapter below, and a write that would carry a live application into a storage
 * somebody has emptied is not a write at all: it is the first sign the session has
 * been torn down externally, and it is answered by tearing down what is left in
 * memory rather than by putting it back.
 */
let keyIsPersisted = false

/** Whether the copy last written — or loaded at boot — carried a live application. */
let persistedLiveSession = false

/** Set while a teardown is running, so its own writes cannot re-enter it. */
let evicting = false

function carriesSession(raw: string | null): boolean {
  if (raw === null) return false
  try {
    const parsed = JSON.parse(raw) as { state?: { appRefNo?: string } }
    return parsed.state?.appRefNo !== undefined
  } catch {
    return false
  }
}

/**
 * True when we have written a live application to this key and something else has
 * since removed it.
 *
 * `persistedLiveSession` is what keeps the guard off the ordinary paths, and it is
 * deliberately the state we LAST WROTE rather than the one being written now. A tab
 * whose stored session was a live one, gone from storage, is the failure case. A
 * first `setAppRefNo` on a tab that had nothing stored is a new application, which
 * is how every journey starts — and `clear()` and `expireIfStale()` write no
 * reference at all, so a teardown landing in an empty storage reads as the two
 * halves agreeing rather than as an eviction.
 */
function wasClearedExternally(name: string): boolean {
  if (!keyIsPersisted || !persistedLiveSession) return false
  return sessionStorage.getItem(name) === null
}

/**
 * Synchronous, and it resets the flags before it clears.
 *
 * `clear()` writes through this same adapter, so the flags have to be down before it
 * runs or its own write would be read as another eviction. Doing it now rather than
 * on a microtask matters: whatever prompted the write is mid-flight, and a session
 * that outlives it by even a tick is one a guard can still read and resume from.
 */
function evict() {
  if (evicting) return
  evicting = true
  keyIsPersisted = false
  persistedLiveSession = false

  try {
    useSessionStore.getState().clear()
  } finally {
    evicting = false
  }
}

const evictionAwareSessionStorage: StateStorage = {
  getItem(name) {
    const raw = sessionStorage.getItem(name)
    // Hydration is also what tells us whether there is anything to lose: a tab that
    // starts with nothing stored cannot have had a session cleared out from under it.
    keyIsPersisted = raw !== null
    persistedLiveSession = carriesSession(raw)
    return raw
  },

  setItem(name, value) {
    if (wasClearedExternally(name)) {
      // The write is dropped rather than written and then cleaned up: putting the
      // applicant's details back on disk, however briefly, is the one thing a machine
      // somebody has just wiped must not do. `evict()` persists the empty session it
      // leaves behind, so storage and memory still agree when this returns.
      evict()
      return
    }

    sessionStorage.setItem(name, value)
    keyIsPersisted = true
    persistedLiveSession = useSessionStore.getState().appRefNo !== undefined
  },

  removeItem(name) {
    sessionStorage.removeItem(name)
    // Our own removal, so there is nothing to notice later.
    keyIsPersisted = false
    persistedLiveSession = false
  },
}

/**
 * The same check, for a tab that is merely sitting there.
 *
 * The write guard only fires when something writes, and a citizen who clears their
 * browsing data and then clicks a link inside the still-open app writes nothing — the
 * in-memory session would carry them onward as if nothing had happened. The expiry
 * watcher calls this on mount, on navigation, on focus and on its poll, which are the
 * moments a tab gets to notice. Returns whether it fired.
 */
export function resetIfStorageCleared(): boolean {
  if (!wasClearedExternally(SESSION_STORAGE_KEY)) return false
  evict()
  return true
}

export const useSessionStore = create<SessionStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      /**
       * Records the eligibility outcome and opens a journey — a full reset, because
       * the ID-number screen is now step 1 and a different ID is a different
       * applicant. Without it, a citizen who returns to the entry screen and enters
       * someone else's number would carry the previous applicant's NPR record,
       * contact details and face-attempt count into the new application.
       */
      startApplication({ idNo, availableProducts }) {
        set({
          ...initialState,
          idNo,
          availableProducts,
          lastActivityAt: new Date().toISOString(),
        })
      },

      setSubmittedAt(submittedAt) {
        set({ submittedAt })
      },

      /**
       * §4.10 finalise output. Deliberately does NOT bump `lastActivityAt`: it is
       * fired by the receipt screen on arrival, not by the citizen doing something,
       * and treating it as activity would extend a session nobody is using.
       */
      setFinaliseResult({ outstandingRequirements, totalPayable, paymentItems }) {
        set({ outstandingRequirements, totalPayable, paymentItems })
      },

      setAppRefNo(appRefNo) {
        set({ appRefNo, lastActivityAt: new Date().toISOString() })
      },

      setPersonalDetails(personalDetails) {
        set({ personalDetails, lastActivityAt: new Date().toISOString() })
      },

      setDeclarationsRequired(declarationsRequired) {
        set({ declarationsRequired })
      },

      setReason(reason) {
        set({ reason })
      },

      setContact(contact) {
        set({ contact, lastActivityAt: new Date().toISOString() })
      },

      setAddress(address) {
        set({ address, lastActivityAt: new Date().toISOString() })
      },

      setOfficer(officer) {
        set({ officer, lastActivityAt: new Date().toISOString() })
      },

      setVacAuthId(vacAuthId) {
        set({ vacAuthId })
      },

      markVfsClientVerificationSaved() {
        set({ vfsClientVerificationSaved: true })
      },

      completeStep(step) {
        const { completedSteps } = get()
        if (completedSteps.includes(step)) return
        set({ completedSteps: [...completedSteps, step], lastActivityAt: new Date().toISOString() })
      },

      /**
       * Only a returned `Verified: false` costs an attempt. A network failure or a
       * timeout must not — a citizen on an unreliable connection should not be
       * pushed toward a branch visit by their signal dropping.
       *
       * Note this counter is UX, not a control: it is trivially reset by clearing
       * sessionStorage, so the limit must also be enforced server-side.
       * See docs/api-gaps.md #5.
       */
      recordFaceAttempt(verified) {
        if (verified) {
          set({ faceVerified: true, lastActivityAt: new Date().toISOString() })
          return
        }

        const faceAttempts = get().faceAttempts + 1
        set({
          faceAttempts,
          faceVerified: false,
          faceBlocked: faceAttempts >= env.faceMaxAttempts,
          lastActivityAt: new Date().toISOString(),
        })
      },

      setPhotoImg(photoImg) {
        set({ photoImg })
      },

      setSignatureImg(signatureImg) {
        set({ signatureImg })
      },

      touch() {
        set({ lastActivityAt: new Date().toISOString() })
      },

      isExpired() {
        const { lastActivityAt } = get()
        if (!lastActivityAt) return false
        const elapsedMinutes = (Date.now() - new Date(lastActivityAt).getTime()) / 60_000
        return elapsedMinutes > env.sessionTtlMinutes
      },

      /**
       * The TTL, applied.
       *
       * A SUBMITTED application is exempt, and that exemption is load-bearing twice
       * over. `clearApplicantData()` has already dropped everything identifying by
       * then, so there is nothing left for the TTL to protect — while what remains
       * (the reference number, and what the citizen still owes) is exactly what
       * §4.10 cannot be called twice to re-read. Expiring the receipt would destroy
       * the only record of a payment the citizen has to make, and the receipt route
       * is a second entry point to the same redirect cycle besides: `setFinaliseResult`
       * deliberately does not bump `lastActivityAt`, so the receipt's clock starts
       * back at `completeStep('declaration')` and thirty minutes of reading it is
       * ordinary.
       */
      expireIfStale() {
        if (!get().isExpired()) return false
        return get().expireNow()
      },

      /**
       * The receipt is exempt here too, and for a sharper reason than the clock
       * case: the receipt's own `cancel` and `verification` calls can meet a lost
       * session, and they must surface as errors on the receipt — never wipe the
       * finalise result, which §4.10 cannot be called again to re-read.
       */
      expireNow() {
        const { submittedAt } = get()
        if (submittedAt) return false

        set({ ...initialState, expiredAt: new Date().toISOString() })
        return true
      },

      clearExpiredFlag() {
        set({ expiredAt: undefined })
      },

      /**
       * Called once the application is submitted.
       *
       * Everything identifying goes — there is no reason for a citizen's NPR record
       * to sit in storage on a shared machine after they are done — but the
       * reference number and the completion flags stay, so the success screen
       * remains readable and the route guards do not eject them from it.
       *
       * ⚠ `idNo` SURVIVES, and did not until 2026-09-10.
       *
       * The receipt carries the Cancel Application button, and §8.1 is the citizen's
       * only route to withdrawing an application once §4.10 has finalised it —
       * clearing the session locally cannot. The VFS gateway rejects that call
       * without the ID number ("The idNo field is required.", HTTP 400), so dropping
       * it here was quietly removing the withdrawal right the button exists to
       * offer: Confirm answered "Something went wrong" and no request was ever made.
       *
       * What still bounds it: `clear()` drops it on every exit from the receipt
       * (Return Home, a successful cancel, an external eviction), it dies with the
       * tab, and `partialize` still writes it only under `persistApplicantDetails`.
       * The NPR record, contact, address and both biometrics are unaffected by this
       * carve-out — they go, as before. See ADR 0008's 2026-09-10 addendum.
       *
       * Nulling it here was also load-bearing for something it had no business
       * governing: `useFinaliseApplication` was `enabled` on `idNo`, so this line
       * was the only thing stopping a refreshed receipt from re-POSTing a
       * non-idempotent §4.10. That guard now reads `submittedAt` instead — see
       * finalise.api.ts. Do not re-add `idNo` here to fix a re-submission bug.
       */
      clearApplicantData() {
        set({
          personalDetails: undefined,
          availableProducts: undefined,
          contact: undefined,
          address: undefined,
          photoImg: undefined,
          signatureImg: undefined,
          // The officer goes too. They are staff rather than the applicant, so this
          // is not a POPIA obligation — but the application is submitted, nothing
          // downstream reads it again, and a named employee left in storage on a
          // shared counter machine after the citizen walks away serves nobody.
          officer: undefined,
          // vacAuthId and its saved flag also SURVIVE, for the reason appRefNo
          // does: they identify nobody, and a cleared flag would license a
          // duplicate write if the citizen returned to a step. vacAuthId in
          // particular is still needed AFTER this runs — the receipt posts the
          // Passed verification outcome against it.
          //
          // outstandingRequirements / totalPayable / paymentItems deliberately
          // SURVIVE. They are what the receipt has to show — "R140.00 still to pay
          // at the centre" — and none of it identifies anybody. `clear()` still
          // drops them, via initialState.
        })
      },

      /**
       * Full teardown: Start over, Cancel, a rejected physical verification, and
       * both exits from the receipt.
       *
       * The server session goes with it. The cookie is bound to this application
       * and, after step 6, to the officer who signed in — on a shared counter
       * machine, the next citizen must not inherit either. `endSession()` is
       * fire-and-forget: it never rejects, and the teardown must not wait on a
       * network it may have lost.
       *
       * Deliberately NOT in `clearApplicantData()`: the receipt still posts the
       * verification outcome after that runs, and it needs the session to do so.
       */
      clear() {
        void endSession()
        set({ ...initialState })
      },
    }),
    {
      name: SESSION_STORAGE_KEY,

      // sessionStorage, not localStorage: the application dies with the tab, is not
      // shared across tabs, and leaves nothing behind on a shared or public machine.
      storage: createJSONStorage(() => evictionAwareSessionStorage),

      /**
       * An explicit allow-list, not a deny-list.
       *
       * `photoImg` and `signatureImg` are absent by construction: adding a field to
       * the state does not add it to storage, so the failure mode is "we forgot to
       * persist something" rather than "we accidentally wrote a citizen's biometrics
       * to disk". A unit test asserts both stay out.
       */
      partialize: (state) => ({
        appRefNo: state.appRefNo,

        /**
         * Unconditional, unlike the identifying fields below: a surrogate key and
         * a boolean identify nobody. They are persisted rather than kept in memory
         * because losing them to a refresh would mean a duplicate POST.
         */
        vacAuthId: state.vacAuthId,
        vfsClientVerificationSaved: state.vfsClientVerificationSaved,

        /**
         * Outside the persistApplicantDetails block below on purpose. That flag
         * governs the *citizen's* personal data; this is the VFS employee operating
         * the counter. And it has to survive two things regardless: the full-page
         * redirect to Microsoft that sets it, and any refresh between here and
         * §4.10 finalise, which is what consumes it as AgentID.
         */
        officer: state.officer,

        completedSteps: state.completedSteps,
        faceAttempts: state.faceAttempts,
        faceVerified: state.faceVerified,
        faceBlocked: state.faceBlocked,
        lastActivityAt: state.lastActivityAt,
        // Persisted so the "your session expired" banner survives the redirect to
        // the ID-number screen, which is a different route and therefore a
        // different mount.
        expiredAt: state.expiredAt,
        reason: state.reason,
        declarationsRequired: state.declarationsRequired,
        availableProducts: state.availableProducts,
        submittedAt: state.submittedAt,

        // Not identifying — an amount and two requirement codes — and
        // unrecoverable, because §4.10 is non-idempotent and cannot be called
        // again to re-read it. Persisted unconditionally so a refresh on the
        // receipt screen still shows the citizen what they owe.
        outstandingRequirements: state.outstandingRequirements,
        totalPayable: state.totalPayable,
        paymentItems: state.paymentItems,

        // Identifying data, behind a flag so a POPIA review can switch it off
        // without a code change. Without it, refresh-resume is impossible.
        ...(env.persistApplicantDetails
          ? {
              idNo: state.idNo,
              personalDetails: state.personalDetails,
              // Contact and address are the same class of identifying data, and
              // are equally unrecoverable: §4.6 and §4.7 both answer 204.
              contact: state.contact,
              address: state.address,
            }
          : {}),
      }),
    }
  )
)
